/**
 * SIMPLIFIED AUTH0 FLOW SAGA
 *
 * STEP 1: Capture Authorization Code
 *
 * Flow:
 * 1. If transfer completed → exit (prevent re-execution)
 * 2. Check for code in URL (?code=XXX) from Auth0 redirect
 *    - If found → extract and dispatch SET_AUTH0_CODE
 * 3. Check for transfer token in sessionStorage
 *    - If found → redirect to Auth0 /authorize
 */

import { put, select, call } from 'redux-saga/effects';
import { clearTokens } from 'ctm-app/client/sagas/utils/tokens';

const OAUTH_STATE_KEY = 'auth0_oauth_state';
const PKCE_VERIFIER_KEY = 'auth0_pkce_verifier';

const base64UrlEncode = (bytes) => {
    let value = '';
    bytes.forEach((byte) => {
        value += String.fromCharCode(byte);
    });
    return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const createPkceVerifier = () => {
    if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
        throw new Error('Web Crypto API is unavailable');
    }

    const randomBytes = new Uint8Array(32);
    crypto.getRandomValues(randomBytes);
    return base64UrlEncode(randomBytes);
};

export const createPkceChallenge = async (verifier) => {
    if (typeof crypto === 'undefined' || !crypto.subtle || typeof TextEncoder === 'undefined') {
        throw new Error('Web Crypto API is unavailable');
    }

    const verifierBytes = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', verifierBytes);
    return base64UrlEncode(new Uint8Array(digest));
};

export const captureAuth0AuthorizationCode = function* (action) {
    try {
        // Exit if transfer already completed
        const transferCompleted = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('auth0_transfer_completed') === 'true';

        if (transferCompleted) {
            return;
        }

        // Extract authorization code from URL
        let authCode = null;
        let urlParams = null;
        if (typeof window !== 'undefined') {
            urlParams = new URLSearchParams(window.location.search);
            authCode = urlParams.get('code');
        }

        if (authCode) {
            const callbackState = urlParams.get('state');
            const expectedState = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(OAUTH_STATE_KEY);

            if (!expectedState || callbackState !== expectedState) {
                if (typeof sessionStorage !== 'undefined') {
                    sessionStorage.removeItem(OAUTH_STATE_KEY);
                    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
                }
                if (typeof window !== 'undefined') {
                    window.history.replaceState({}, '', window.location.pathname);
                }
                yield put({
                    type: 'SET_AUTH0_ERROR',
                    error: 'Authorization callback state did not match',
                    severity: 'error'
                });
                return;
            }

            yield put({
                type: 'SET_AUTH0_CODE',
                value: authCode,
                timestamp: new Date().toISOString()
            });
            // Clean URL
            if (typeof window !== 'undefined') {
                window.history.replaceState({}, '', window.location.pathname);
            }
            return;
        }

        // Check for transfer token
        let sessionTransferToken = null;
        if (typeof sessionStorage !== 'undefined') {
            sessionTransferToken = sessionStorage.getItem('auth0_session_transfer_token');
        }

        if (!sessionTransferToken) {
            return;
        }

        // Prevent re-redirect if already attempted
        const redirectAttempted = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('auth0_redirect_attempted') === 'true';

        if (redirectAttempted) {
            return;
        }

        // Load Auth0 configuration from Redux
        const serverConfig = yield select((state) => state.getIn(['serverConfig'])?.toJS?.() || {});

        const { auth0Domain, auth0ClientId, auth0RedirectUri, auth0ApiIdentifier } = serverConfig;

        if (!auth0Domain || !auth0ClientId || !auth0RedirectUri) {
            throw new Error(`Missing Auth0 configuration: domain=${auth0Domain}, clientId=${auth0ClientId}, redirectUri=${auth0RedirectUri}`);
        }

        // Build authorization URL
        const authorizeUrl = new URL(`https://${auth0Domain}/authorize`);
        authorizeUrl.searchParams.set('response_type', 'code');
        authorizeUrl.searchParams.set('client_id', auth0ClientId);
        authorizeUrl.searchParams.set('redirect_uri', auth0RedirectUri);
        authorizeUrl.searchParams.set('scope', 'openid email profile offline_access');
        authorizeUrl.searchParams.set('audience', auth0ApiIdentifier);
        authorizeUrl.searchParams.set('session_transfer_token', sessionTransferToken);

        const oauthState = createPkceVerifier();
        const verifier = createPkceVerifier();
        const challenge = yield call(createPkceChallenge, verifier);
        authorizeUrl.searchParams.set('state', oauthState);
        authorizeUrl.searchParams.set('code_challenge', challenge);
        authorizeUrl.searchParams.set('code_challenge_method', 'S256');

        // Mark redirect attempted to prevent re-execution
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(OAUTH_STATE_KEY, oauthState);
            sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
            sessionStorage.setItem('auth0_redirect_attempted', 'true');
        }

        // Redirect to Auth0 login
        if (typeof window !== 'undefined') {
            window.location.assign(authorizeUrl.toString());
        }
    } catch (error) {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('auth0_token_exchange_failed', 'true');
        }
        console.error('[AUTH0] STEP 1 Error:', error?.message || error);
        yield put({
            type: 'SET_AUTH0_ERROR',
            error: error?.message || 'Authorization code capture failed',
            severity: 'error'
        });
    }
};

/**
 * SIMPLIFIED AUTH0 TOKEN EXCHANGE
 *
 * STEP 2: Exchange Authorization Code for Access Token
 *
 * Flow:
 * 1. If transfer completed → exit
 * 2. Get authorization code from Redux
 * 3. POST to Auth0 token endpoint with: code, client_id, redirect_uri
 * 4. Receive: access_token, refresh_token, expires_in
 * 5. Dispatch SET_AUTH0_TOKEN with response
 */

export const exchangeAuth0CodeForToken = function* (action) {
    try {
        // Exit if transfer already completed
        const transferCompleted = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('auth0_transfer_completed') === 'true';

        if (transferCompleted) {
            return;
        }

        // clear existing tokens from redux if continuing with session transfer token exchange
        yield call(clearTokens);

        // Retrieve authorization code from Redux
        const authCode = yield select((state) => state.getIn(['vertical', 'quote', 'authUser', 'code']));

        if (!authCode) {
            return;
        }

        const verifier = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PKCE_VERIFIER_KEY);

        if (!verifier) {
            throw new Error('PKCE verifier is missing');
        }

        // Load Auth0 configuration from Redux
        const serverConfig = yield select((state) => state.getIn(['serverConfig'])?.toJS?.() || {});

        const { auth0Domain, auth0ClientId, auth0RedirectUri } = serverConfig;

        if (!auth0Domain || !auth0ClientId || !auth0RedirectUri) {
            throw new Error('Auth0 configuration missing');
        }

        // Prepare token exchange request
        const tokenUrl = `https://${auth0Domain}/oauth/token`;
        const tokenRequest = {
            grant_type: 'authorization_code',
            client_id: auth0ClientId,
            code: authCode,
            redirect_uri: auth0RedirectUri,
            code_verifier: verifier
        };

        // Exchange authorization code for access token
        const response = yield call(fetch, tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tokenRequest)
        });

        if (!response.ok) {
            let errorData = {};
            try {
                errorData = yield call([response, 'json']);
            } catch (e) {
                // Response not JSON
            }

            const errorMessage = errorData?.error_description || errorData?.error || `HTTP ${response.status}: ${response.statusText}`;

            throw new Error(`Token exchange failed: ${errorMessage}`);
        }

        const tokenData = yield call([response, 'json']);

        if (!tokenData?.access_token) {
            throw new Error('No access_token in response');
        }

        // Store token in Redux
        yield put({
            type: 'SET_AUTH0_TOKEN',
            token: tokenData.access_token,
            refreshToken: tokenData.refresh_token || null,
            expiresIn: tokenData.expires_in || 3600,
            idToken: tokenData.id_token || null,
            timestamp: new Date().toISOString()
        });
    } catch (error) {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('auth0_token_exchange_failed', 'true');
        }
        console.error('[AUTH0] STEP 2 Error:', error?.message || error);
        yield put({
            type: 'SET_AUTH0_ERROR',
            error: error?.message || 'Token exchange failed',
            severity: 'error'
        });
    }
};

// Export for saga listener registration
export const step1Saga = [['CAPTURE_AUTH0_CODE'], captureAuth0AuthorizationCode];

export const step2Saga = [['SET_AUTH0_CODE'], exchangeAuth0CodeForToken];

export default [step1Saga, step2Saga];
